import { alarmApi, configurationApi } from '@/api';
import { ColorPicker, DraggableTable, Fieldset, getColumnConfigurationToSave, Modal, Select } from '@/components/common';
import { handleResponse, ScrollType } from '@/Helper';
import { SecuredComponents } from '@/model/AccountModel';
import { AlarmColorType } from '@/model/AlarmModel';
import { BaseColumns, GridColumn, PersistedObjectId, ResponseStatusCode, SelectOptions } from '@/model/CommonModel';
import { ColumnConfiguration, ProfileSetting } from '@/model/ConfigurationModel';
import { Logger } from '@/model/LoggingModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setAlarmColors } from '@/store/alarm/actions';
import { selectAlarmColors } from '@/store/alarm/selectors';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Spin, Table } from 'antd';
import { Color } from 'antd/es/color-picker';
import { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { addExtraColumns, defaultAlarmColors, getNewAlarmColorObject, unsupportedColumnKeys } from '../helpers';
import styles from './alarmViewerProperties.module.scss';

const scroll: ScrollType = { x: 'max-content', y: '40vh' };

type DataType = { index: number; Header: string } & GridColumn & BaseColumns;

type Props = {
	onHideModal: () => void;
	dispatchAction: (skipRender: boolean) => void;
	isAcknowledgedAlarmTable: boolean;
};

const component: React.FC<Props> = ({ onHideModal, dispatchAction, isAcknowledgedAlarmTable }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [dataSourceTable, setDataSourceTable] = useState<DataType[]>([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const [columnConfiguration, setColumnConfiguration] = useState<ColumnConfiguration>(undefined);
	const [hiddenColumns, setHiddenColumns] = useState<GridColumn[]>([]);
	const [error, setError] = useState<boolean>(false);
	const [colorOptions, setColorOptions] = useState<SelectOptions<string>[]>([]);
	const [colorTypeId, setColorTypeId] = useState<string>(AlarmColorType.Background.toString());
	const [hasColorChanged, setHasColorChanged] = useState<boolean>(false);
	const [localColors, setLocalColors] = useState<Record<AlarmColorType, string>>(defaultAlarmColors);

	const alarmColors: Record<AlarmColorType, string> = useStoreSelector<Record<AlarmColorType, string>>(selectAlarmColors);
	//#endregion

	useEffect(() => {
		Promise.all([alarmApi.getAlarmsColumnsConfiguration(isAcknowledgedAlarmTable), alarmApi.getAlarmColorLabels()])
			.then(responses => {
				const [columnConfigurationResponse, alarmLabelsResponse] = responses;
				if (!handleResponse(columnConfigurationResponse) && !handleResponse(alarmLabelsResponse)) {
					setColorOptions(
						alarmLabelsResponse.Entity.map<SelectOptions<string>>((item, index) => ({
							label: item.Label,
							value: item.ColorType.toString(),
							id: `alarmColorDropdownOption-${index}`,
						}))
					);

					const columnConfiguration = columnConfigurationResponse.Entity;
					const hiddenColumns = getHiddenColumns(columnConfiguration.Columns);
					setHiddenColumns(hiddenColumns);
					columnConfiguration.Columns = columnConfiguration.Columns.filter(x => !x.IsAuxColumn && !hiddenColumns.some(c => x.Key === c.Key));
					setColumnConfiguration(columnConfiguration);
					updateDataSourceTableAndSelectedRowKeys(columnConfiguration.Columns, hiddenColumns);
					setIsLoading(false);
				} else {
					onHideModal();
				}
			})
			.catch(err => {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			});

		setLocalColors(getNewAlarmColorObject(alarmColors, true));
	}, []);

	useEffect(() => {
		if (columnConfiguration !== undefined) {
			updateDataSourceTableAndSelectedRowKeys(columnConfiguration.Columns, hiddenColumns);
		}
	}, [columnConfiguration]);

	useEffect(() => {
		if (error && selectedRowKeys.length > 0) {
			setError(false);
		}
	}, [selectedRowKeys]);

	const columns: ColumnsType<DataType> = [
		{
			title: _('Column'),
			dataIndex: 'Header',
		},
		{
			key: 'sort',
			title: _('Order'),
			dataIndex: 'sort',
			width: 55,
		},
	];

	const getHiddenColumns = (columnConfiguration: GridColumn[]): GridColumn[] => {
		const unsupportedColumns: GridColumn[] = [];
		unsupportedColumnKeys.forEach(unsupportedColumnKey => {
			const unsupportedColumn: GridColumn = columnConfiguration.find(x => x.Key === unsupportedColumnKey);
			if (unsupportedColumn) {
				unsupportedColumns.push({ ...unsupportedColumn });
			}
		});

		return unsupportedColumns;
	};

	const updateDataSourceTableAndSelectedRowKeys = (columnConfiguration: GridColumn[], hiddenColumns: GridColumn[]) => {
		const dataTable = columnConfiguration
			.filter(x => !x.IsAuxColumn && !hiddenColumns.some(c => x.Key === c.Key))
			.map((column: GridColumn, index) => ({
				...column,
				index,
				Header: column.Header,
				key: index.toString(),
			}));
		setDataSourceTable(dataTable);
		setSelectedRowKeys(dataTable.filter(f => f.Visible === true).map(m => m.index.toString()));
	};

	const onSelectChangeCheckBox = (selectedRowKeys: string[]) => {
		const updateItems: GridColumn[] = dataSourceTable.map<GridColumn>(m => {
			const found = selectedRowKeys.find(f => f === m.index.toString());
			return {
				...m,
				Order: dataSourceTable.indexOf(m) + 1,
				Visible: found !== undefined ? true : false,
			};
		});
		setSelectedRowKeys(selectedRowKeys);
		setColumnConfiguration({ ...columnConfiguration, Columns: updateItems });
	};

	const handleOnSaveColumnConfiguration = e => {
		if (selectedRowKeys.length === 0) {
			setError(true);
			return;
		}

		let clonedState: ColumnConfiguration = { ...columnConfiguration };
		if (hiddenColumns.length) {
			clonedState.Columns = addExtraColumns(clonedState.Columns, hiddenColumns);
		}
		const alarmColorsSetting: ProfileSetting = {
			Name: 'Colors',
			ObjectId: PersistedObjectId.AlarmViewer,
			Value: Object.values(localColors)
				.map<string>((x, index) => {
					if (index === 1) {
						//Unused ForeColor
						return `#FFFFFF,${x}`;
					}

					return x;
				})
				.toString(),
		};

		clonedState = getColumnConfigurationToSave(clonedState);
		Promise.all([
			configurationApi.saveColumnsConfiguration(clonedState, SecuredComponents.Alarm_Viewer),
			alarmApi.saveAlarmProperties([alarmColorsSetting]),
		])
			.then(responses => {
				const [saveColumnsResponse, savePropertiesResponse] = responses;

				if (
					saveColumnsResponse.ResponseStatusCode === ResponseStatusCode.Success &&
					savePropertiesResponse.ResponseStatusCode === ResponseStatusCode.Success
				) {
					dispatch(setAlarmColors(localColors));
					dispatchAction(hasColorChanged);
					onHideModal();
				} else if (saveColumnsResponse.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
					setError(true);
				} else {
					handleResponse(saveColumnsResponse);
					handleResponse(savePropertiesResponse);
				}
			})
			.catch(err => {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			});
	};

	const rowSelection: TableRowSelection<DataType> = {
		selectedRowKeys,
		onChange: onSelectChangeCheckBox,
		selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
	};

	const onSortEnd = ({ oldIndex, newIndex }) => {
		if (oldIndex !== newIndex) {
			const newData = arrayMove([].concat(dataSourceTable), oldIndex, newIndex).filter(el => !!el);
			const updateItems: GridColumn[] = newData.map<GridColumn>(m => {
				return {
					...m,
					Order: newData.indexOf(m) + 1,
				};
			});
			setDataSourceTable(newData);
			setColumnConfiguration({ ...columnConfiguration, Columns: updateItems });
		}
	};

	const currentColor = localColors[colorTypeId];
	const handleOnChangeColorType = value => setColorTypeId(value);
	const handleOnChangeColor = (value: Color) => {
		batch(() => {
			setHasColorChanged(true);
			setLocalColors(prevState => ({ ...prevState, [colorTypeId]: value.toHexString() }));
		});
	};

	return (
		<Modal
			onCancel={onHideModal}
			onClickOk={() => null}
			width={'50vw'}
			title={_('AlarmViewerProperties')}
			visible={true}
			customZoomClass={styles.customZoomClass}
			footer={[
				<Button id='saveColumnConfigurationButton' key='ok' type='primary' onClick={handleOnSaveColumnConfiguration} disabled={error}>
					{_('SaveChanges')}
				</Button>,
				<Button id='cancelColumnConfigurationButton' key='cancel' onClick={onHideModal}>
					{_('Cancel')}
				</Button>,
			]}>
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
				<div className={styles.container}>
					<Fieldset title={_('Colors')}>
						<div className={styles.colorGrid}>
							<label className={styles.selectLabel} htmlFor='alarmViewerColorSelect'>
								{_('AlarmViewerColors')}
							</label>
							<div className={styles.colorPickerContainer}>
								<ColorPicker value={currentColor} onChange={handleOnChangeColor} horizontal />
							</div>
							<Select
								id='alarmViewerColorSelect'
								options={colorOptions}
								value={colorTypeId}
								onChange={handleOnChangeColorType}
								className={styles.colorSelect}
								showSearch={true}
								maxSearchLength={50}
							/>
						</div>
						<br />
						<div>{`${_('Note')}: ${_('ClickOnColorBoxToChange')}`}</div>
					</Fieldset>
					<Fieldset title={_('ColumnHeaders')} className={styles.columnHeadersContainer}>
						<div>
							<p>{_('ConfigureColumnInstructions')}</p>
						</div>
						<div className={cx(styles.tableContainer, { [styles.error]: error })}>
							<DraggableTable<DataType>
								id='columnConfigurationTable'
								scroll={scroll}
								pagination={false}
								dataSource={dataSourceTable}
								columns={columns}
								rowSelection={rowSelection}
								onSortEnd={onSortEnd}
							/>
						</div>
						{error && (
							<span className={styles.error} id='columnConfigurationErrorMessage'>
								{_('AtLeastOneItem')}
							</span>
						)}
					</Fieldset>
				</div>
			</Spin>
		</Modal>
	);
};

const AlarmViewerProperties = React.memo(component);

export { AlarmViewerProperties };
